import config from "../config";

/**
 * 通用 in-memory CRUD mock 註冊器。
 *
 * 為單一資源在共用的 axios-mock-adapter 上註冊 REST 端點：
 *   GET    {base}           列表（支援 page/pageSize/keyword/欄位過濾）
 *   GET    {base}/:id       單筆（找不到回 404）
 *   POST   {base}           新增（自動配 id，approvalStatus 預設 'pending'）
 *   PUT    {base}/:id       更新
 *   DELETE {base}/:id       刪除（可用 canDelete 模擬「刪除阻擋」→ 409）
 *
 * 狀態模擬（掛在 query string，方便驗 DoD 狀態頁）：
 *   ?_mock=empty  → 列表回空（查無結果）
 *   ?_mock=error  → 回 500（錯誤態）
 *   ?_mock=slow   → 延遲 12s（>10s 超時防呆 / 骨架屏）
 *
 * 列表回應格式：{ data: [...], total, page, pageSize }
 * 單筆/新增/更新：回該筆物件。
 */

const latency = () => config.MOCK_LATENCY;

/**
 * 資源註冊表 —— 讓審核引擎能在核准後回寫目標資料。
 *
 * 每個資源的 store 原本封在 registerResource 的 closure 裡，外面完全摸不到，
 * 所以審核引擎雖然有 resource/recordId 卻無法套用結果，前端只好自己搶著寫，
 * 才會出現「按下結案當下就鎖死、退件無法自救」。這張表就是那條缺的線。
 */
const resourceStores = new Map();

/** 依資源名稱取得存取介面；未註冊時回 null（呼叫端要能容忍） */
export const getResourceStore = (name) => resourceStores.get(name) || null;

function reply(status, body, delay) {
  return new Promise((resolve) => {
    setTimeout(() => resolve([status, body]), delay);
  });
}

function idFromUrl(url) {
  const clean = url.split("?")[0];
  return decodeURIComponent(clean.substring(clean.lastIndexOf("/") + 1));
}

function applyMockFlag(params) {
  const flag = params && params._mock;
  if (flag === "empty") return { empty: true };
  if (flag === "error") return { error: true };
  if (flag === "slow") return { slow: true };
  return {};
}

/**
 * @param {object} mock  共用的 MockAdapter 實例
 * @param {object} opts
 * @param {string} opts.base          資源 base 路徑，如 "/api/users"
 * @param {Array}  opts.seed          初始資料（會被複製，run-time 可增刪改）
 * @param {string} [opts.idKey="id"]  主鍵欄位
 * @param {string[]} [opts.searchFields=[]] keyword 會比對的欄位
 * @param {function} [opts.canDelete] (item, store) => true | string(阻擋原因)
 * @param {object} [opts.importFields] 批次匯入設定：
 *        { required: string[], unique?: string, validateBatch?: function } → 註冊 POST {base}/import
 *        validateBatch(rows, store) => [{index, field, reason}]：rows 是已通過必填檢查的列
 *        (依原順序、不含未過必填的列)，index 是相對 rows 的位置；用來做「列與列互相衝突」
 *        這類單欄位驗證顧不到的批次級檢查（例：硬體匯入的 U 位衝突，ticket 29）。
 */
export function registerResource(mock, opts) {
  const {
    base,
    name,
    seed = [],
    idKey = "id",
    searchFields = [],
    canDelete,
    importFields,
    // 寫入前的收尾:可以改 record,回傳字串代表擋下這次寫入(轉成 400)
    beforeCreate,
    beforeUpdate,
    // 讀取時才算出來的衍生欄位(例如資產的管理者:真相在負責關係上,資產自己不存)
    decorate,
  } = opts;

  // 複製一份當作可變的記憶體資料庫
  let store = seed.map((r) => ({ ...r }));
  let nextId =
    store.reduce((max, r) => Math.max(max, Number(r[idKey]) || 0), 0) + 1;

  const byId = new RegExp(`^${base}/[^/]+$`);

  // 對外的樣子 = 存的資料 + 衍生欄位。衍生的東西不進 store,
  // 才不會又變成第二份真相。
  const view = (row) => (typeof decorate === "function" ? decorate(row) : row);

  // 有給 name 才登記到註冊表（供審核引擎回寫）
  if (name) {
    resourceStores.set(name, {
      // 注意:store 是 seed 的副本,run-time 新增的資料只在這裡。
      // 需要看到「目前真正有哪些資料」一律走這個介面,不要去讀 db/ 的 seed 陣列。
      all: () => store,
      find: (id) => store.find((r) => String(r[idKey]) === String(id)) || null,
      add: (row) => {
        const record = { ...row, [idKey]: nextId++ };
        store.push(record);
        return record;
      },
      // 刪除阻擋的規則露出來共用 —— 刪除要送審(ticket 08),
      // 檢查得在「送出單子」那一刻做,不能等到真的刪才擋。
      blockedReason: (id) => {
        if (typeof canDelete !== "function") return null;
        const row = store.find((r) => String(r[idKey]) === String(id));
        if (!row) return null;
        // 簽名是 (item, store) —— 少傳 store 的話,用到它的規則會在送審時放行、刪除時才 409
        const verdict = canDelete(row, store);
        return typeof verdict === "string" ? verdict : null;
      },
      remove: (id) => {
        const idx = store.findIndex((r) => String(r[idKey]) === String(id));
        if (idx !== -1) store.splice(idx, 1);
      },
      patch: (id, fields) => {
        const idx = store.findIndex((r) => String(r[idKey]) === String(id));
        if (idx === -1) return null;
        store[idx] = { ...store[idx], ...fields, [idKey]: store[idx][idKey] };
        return store[idx];
      },
    });
  }

  // —— 列表 ——
  mock.onGet(base).reply((cfg) => {
    const params = cfg.params || {};
    const { empty, error, slow } = applyMockFlag(params);
    if (error) return reply(500, { message: "模擬伺服器錯誤" }, latency());
    const delay = slow ? 12000 : latency();
    if (empty) {
      return reply(200, { data: [], total: 0, page: 1, pageSize: 10 }, delay);
    }

    // 先套 decorate 再過濾:decorate 出來的欄位(如機櫃的 manager)也要能被 keyword 搜到
    let rows = store.slice().map(view);

    // keyword 模糊搜尋
    const keyword = (params.keyword || "").trim();
    if (keyword && searchFields.length) {
      const kw = keyword.toLowerCase();
      rows = rows.filter((r) =>
        searchFields.some((f) =>
          String(r[f] ?? "").toLowerCase().includes(kw)
        )
      );
    }

    // 其餘 query 當作等值欄位過濾（略過分頁與保留字）
    const reserved = ["page", "pageSize", "keyword", "_mock", "sort", "order"];
    Object.keys(params).forEach((key) => {
      if (reserved.includes(key)) return;
      const val = params[key];
      if (val === undefined || val === "" || val === null) return;
      rows = rows.filter((r) => String(r[key]) === String(val));
    });

    // 表頭排序(issue 05):sort=欄位 key、order=asc|desc;真後端同介面
    if (params.sort) {
      const dir = params.order === "desc" ? -1 : 1;
      rows = [...rows].sort(
        (a, b) => String(a[params.sort] ?? "").localeCompare(String(b[params.sort] ?? ""), "zh-Hant") * dir
      );
    }

    const total = rows.length;
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 10;
    const startIdx = (page - 1) * pageSize;
    const data = rows.slice(startIdx, startIdx + pageSize);

    return reply(200, { data, total, page, pageSize }, delay);
  });

  // —— 單筆 ——
  mock.onGet(byId).reply((cfg) => {
    const id = idFromUrl(cfg.url);
    const item = store.find((r) => String(r[idKey]) === id);
    return item
      ? reply(200, view(item), latency())
      : reply(404, { message: "查無此資料" }, latency());
  });

  // —— 新增 ——
  mock.onPost(base).reply((cfg) => {
    const payload = cfg.data ? JSON.parse(cfg.data) : {};
    const record = {
      ...payload,
      [idKey]: nextId++,
      approvalStatus: payload.approvalStatus || "pending", // 送出即進審核
      createdAt: new Date().toISOString(),
    };
    // 擋下就不能留半筆資料,所以在 push 之前跑
    if (typeof beforeCreate === "function") {
      const problem = beforeCreate(record, payload);
      if (problem) return reply(400, { message: problem }, latency());
    }
    store.push(record);
    return reply(200, view(record), latency());
  });

  // —— 更新 ——
  mock.onPut(byId).reply((cfg) => {
    const id = idFromUrl(cfg.url);
    const payload = cfg.data ? JSON.parse(cfg.data) : {};
    const idx = store.findIndex((r) => String(r[idKey]) === id);
    if (idx === -1) return reply(404, { message: "查無此資料" }, latency());
    // 審核期間鎖住:單子還沒有結果之前,誰都不能偷改這筆(ticket 08)。
    // 條件是「locked 且審核中」而不是只看 locked —— 有些資源用 locked 表達
    // 自己的語意(例如已結案),那種要走該資源自己的 canDelete 訊息。
    if (store[idx].locked && store[idx].approvalStatus === "pending") {
      return reply(409, { message: "這筆資料有審核單進行中，結案前無法異動。" }, latency());
    }
    const next = { ...store[idx], ...payload, [idKey]: store[idx][idKey] };
    if (typeof beforeUpdate === "function") {
      const problem = beforeUpdate(next, payload);
      if (problem) return reply(400, { message: problem }, latency());
    }
    store[idx] = next;
    return reply(200, view(store[idx]), latency());
  });

  // —— 刪除（含刪除阻擋）——
  mock.onDelete(byId).reply((cfg) => {
    const id = idFromUrl(cfg.url);
    const idx = store.findIndex((r) => String(r[idKey]) === id);
    if (idx === -1) return reply(404, { message: "查無此資料" }, latency());
    // 有審核單進行中就不給硬刪 —— 否則刪除送審那條路等於白做(ticket 08)
    if (store[idx].locked && store[idx].approvalStatus === "pending") {
      return reply(409, { message: "這筆資料有審核單進行中，結案前無法刪除。" }, latency());
    }

    if (typeof canDelete === "function") {
      const verdict = canDelete(store[idx], store);
      if (verdict !== true) {
        return reply(
          409,
          { message: verdict || "此資料仍被引用，無法刪除" },
          latency()
        );
      }
    }
    const [removed] = store.splice(idx, 1);
    return reply(200, { [idKey]: removed[idKey], deleted: true }, latency());
  });

  // —— 批次匯入（整批一單；逐欄位錯誤回報 + 取代提示）——
  if (importFields) {
    const { required = [], unique, validateBatch } = importFields;
    mock.onPost(`${base}/import`).reply((cfg) => {
      const payload = cfg.data ? JSON.parse(cfg.data) : {};
      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      const failed = [];
      const toReplace = [];
      const toCreate = [];
      const passed = []; // 必填都過的列,保留原始 index 才能把批次驗證的失敗對回正確的列

      rows.forEach((row, index) => {
        // 必填欄位逐欄檢查
        const missing = required.filter(
          (f) => row[f] === undefined || row[f] === null || row[f] === ""
        );
        if (missing.length) {
          missing.forEach((f) =>
            failed.push({ index, field: f, reason: `「${f}」為必填` })
          );
          return;
        }
        passed.push({ index, row });
      });

      // 批次級驗證(ticket 29,例如硬體 U 位衝突):只對必填已過的列做,
      // 回傳的 index 是相對 passed 陣列的位置,這裡換算回原始列號。
      let candidates = passed;
      if (typeof validateBatch === "function") {
        const batchFailed = validateBatch(passed.map((p) => p.row), store) || [];
        const failedAt = new Set(batchFailed.map((f) => f.index));
        batchFailed.forEach((f) => {
          const { index: originalIndex } = passed[f.index];
          failed.push({ index: originalIndex, field: f.field, reason: f.reason });
        });
        candidates = passed.filter((_, i) => !failedAt.has(i));
      }

      candidates.forEach(({ row }) => {
        // 取代提示：unique 欄位已存在 → 標記為取代
        if (unique && store.some((r) => String(r[unique]) === String(row[unique]))) {
          toReplace.push({ row });
        } else {
          toCreate.push({ row });
        }
      });

      // 實際寫入（成功者才進 store，approvalStatus=pending 代表整批送審）
      toCreate.forEach(({ index, row }) => {
        const record = { ...row, [idKey]: nextId++, approvalStatus: "pending" };
        // 匯入與單筆新增走同一條收尾,否則試算表裡的管理者欄位會被生吞
        const problem =
          typeof beforeCreate === "function" ? beforeCreate(record, row) : null;
        if (problem) {
          failed.push({ index, field: "-", reason: problem });
          return;
        }
        store.push(record);
      });
      toReplace.forEach(({ index, row }) => {
        const i = store.findIndex((r) => String(r[unique]) === String(row[unique]));
        if (i === -1) return;
        const next = { ...store[i], ...row, approvalStatus: "pending" };
        const problem =
          typeof beforeUpdate === "function" ? beforeUpdate(next, row) : null;
        if (problem) {
          failed.push({ index, field: "-", reason: problem });
          return;
        }
        store[i] = next;
      });

      return reply(
        200,
        {
          total: rows.length,
          success: toCreate.length,
          replaced: toReplace.length,
          failedCount: failed.length,
          failed,
        },
        latency()
      );
    });
  }

  return {
    base,
    name,
    reset: () => {
      store = seed.map((r) => ({ ...r }));
    },
  };
}
